import React, { useState, useEffect, useRef } from 'react';
import { useBTDataImport } from '../hooks/useBTDataImport';
import { formatUTCInputToTZKey } from '../utils/timeUtils';

interface DataImportSectionProps {
  title: string;
  tableName: string;
  onDataImported?: (importResult?: any) => void;
}

const DataImportSection: React.FC<DataImportSectionProps> = ({
  title,
  tableName,
  onDataImported
}) => {
  const getNowUTCForInput = (): string => {
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const yyyy = now.getUTCFullYear();
    const mm = pad(now.getUTCMonth() + 1);
    const dd = pad(now.getUTCDate());
    const hh = pad(now.getUTCHours());
    const mi = pad(now.getUTCMinutes());
    const ss = pad(now.getUTCSeconds());
    return `${yyyy}-${mm}-${dd}T${hh}:${mi}:${ss}`;
  };
  
  // 시간 상태 (UTC 기반 ISO 문자열)
  const [startDateTime, setStartDateTime] = useState<string>(() => getNowUTCForInput());
  const [endDateTime, setEndDateTime] = useState<string>(() => getNowUTCForInput());

  const [dataCount, setDataCount] = useState<number | null>(null);
  const [countLoading, setCountLoading] = useState<boolean>(false);
  const [countError, setCountError] = useState<string | null>(null);
  const [lastImportResult, setLastImportResult] = useState<{ details: { table: string; imported: number; total?: number }[]; allSuccess: boolean } | null>(null);
  const [lastDevToProdResult, setLastDevToProdResult] = useState<{ total: number; details: { table: string; imported: number }[] } | null>(null);
  const [exportLoading, setExportLoading] = useState<boolean>(false);

  // RDS 데이터 임포트
  const { importData, loading: importLoading, error: importError } = useBTDataImport();

  // 임포트 시작 시각 (fetch 연결이 끊겼을 때 폴링 fallback에서 사용)
  const importStartTimeRef = useRef<number | null>(null);

  // 폴링 fallback: fetch 연결이 끊겨도 이력 DB를 주기적으로 확인해 완료 감지
  useEffect(() => {
    if (!countLoading) return;

    const intervalId = setInterval(async () => {
      if (!importStartTimeRef.current) return;
      // 빠른 임포트(kiwoom_ranking_fluctuation_daily ~4분)가 먼저 끝날 수 있으므로
      // 10분 이상 경과한 뒤부터만 폴링 완료 판정 (live_eval_snapshot ~20분 대기용)
      if (Date.now() - importStartTimeRef.current < 10 * 60 * 1000) return;
      try {
        const res = await fetch(
          `/mercury-backend/bt/import-history?tableName=${encodeURIComponent(tableName)}`
        );
        if (!res.ok) return;
        const { data: histories = [] } = await res.json();
        if (!histories.length) return;
        const latestTime = new Date(histories[0].import_time).getTime();
        if (latestTime > importStartTimeRef.current) {
          // 새 임포트 이력 감지 → fetch 연결 끊김에도 완료 처리
          setCountLoading(false);
          setLastImportResult({
            details: [{ table: `${tableName} (연결 끊김 후 이력에서 감지)`, imported: histories[0].total_count ?? 1 }],
            allSuccess: true,
          });
          onDataImported?.();
        }
      } catch { /* 폴링 오류 무시 */ }
    }, 30_000);

    return () => clearInterval(intervalId);
  }, [countLoading, tableName, onDataImported]);

  const handleDataImport = async () => {
    if (!startDateTime || !endDateTime) {
      setCountError('시작시간과 종료시간을 선택해주세요');
      return;
    }
    setCountLoading(true);
    setCountError(null);
    setLastImportResult(null);
    importStartTimeRef.current = Date.now();
    const ensureZ = (s: string) => (s && !s.endsWith('Z') && !/[-+]\d{2}:\d{2}$/.test(s) ? s + 'Z' : s);
    const start = ensureZ(startDateTime);
    const end = ensureZ(endDateTime);
    try {
      // 1. 원격 자원(RDS, MongoDB, OPC) 연결 가능 여부 먼저 검사
      const checkRes = await fetch('/mercury-backend/bt/check-prod-resources');
      if (!checkRes.ok) {
        throw new Error(`연결 검사 실패: HTTP ${checkRes.status}`);
      }
      const check = await checkRes.json() as { ok?: boolean; rds?: string; rdsError?: string; mongodb?: string; mongodbError?: string; opc?: string; opcError?: string };
      if (!check.ok) {
        const parts: string[] = [];
        if (check.rds === 'error' && check.rdsError) parts.push(`RDS: ${check.rdsError}`);
        if (check.mongodb === 'error' && check.mongodbError) parts.push(`MongoDB: ${check.mongodbError}`);
        if (check.opc === 'error' && check.opcError) parts.push(`OPC: ${check.opcError}`);
        throw new Error('원격 자원 연결 실패. 임포트를 중단합니다.\n' + parts.join('\n'));
      }
      // 2. 검사 통과 후 RDS / MongoDB 0c·0d·1h / OPC 임포트를 병렬 실행
      const body = (c: string) => JSON.stringify({ collection: c, startDateTime: start, endDateTime: end });
      const failedResponse = (e: unknown) => ({ ok: false, status: 0, statusText: String(e), text: async () => String(e), json: async () => ({}) } as unknown as Response);
      const [response, r0c, r0d, r1h, rOpc, rTradeStock, rLiveEval, rTimeline, rProdLogs, rProdStockLogs] = await Promise.all([
        importData(startDateTime, endDateTime, tableName),
        fetch('/mercury-backend/bt/import-mongo-prod-to-dev', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: body('kiwoom_orderbook_0c'),
        }),
        fetch('/mercury-backend/bt/import-mongo-prod-to-dev', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: body('kiwoom_orderbook_0d'),
        }),
        fetch('/mercury-backend/bt/import-mongo-prod-to-dev', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: body('kiwoom_1h'),
        }),
        fetch('/mercury-backend/bt/import-opc-jsonl', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ startDateTime: start, endDateTime: end }),
        }),
        fetch('/mercury-backend/bt/import-trade-stock', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ startDateTime: start, endDateTime: end }),
        }),
        fetch('/mercury-backend/bt/import-mongo-prod-to-dev', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: body('live_eval_snapshot'),
        }),
        fetch('/mercury-backend/bt/import-mongo-prod-to-dev', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: body('ka10027_state_timeline'),
        }),
        fetch('/mercury-backend/bt/import-prod-logs', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ startDateTime: start, endDateTime: end }),
        }).catch(failedResponse),
        fetch('/mercury-backend/bt/import-prod-stock-logs', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ startDateTime: start, endDateTime: end }),
        }).catch(failedResponse),
      ]);
      // 1) HTTP 레벨 실패 먼저 체크 (prod 로그 임포트는 보조 기능 — 실패해도 전체 임포트 중단하지 않음)
      if (!r0c.ok) throw new Error(`MongoDB 0c: HTTP ${r0c.status}`);
      if (!r0d.ok) throw new Error(`MongoDB 0d: HTTP ${r0d.status}`);
      if (!r1h.ok) throw new Error(`MongoDB 1h: HTTP ${r1h.status}`);
      if (!rOpc.ok) throw new Error(`OPC JSONL: HTTP ${rOpc.status} ${await rOpc.text()}`);
      if (!rTradeStock.ok) throw new Error(`trade_stock: HTTP ${rTradeStock.status} ${await rTradeStock.text()}`);
      if (!rLiveEval.ok) throw new Error(`live_eval_snapshot: HTTP ${rLiveEval.status}`);
      if (!rTimeline.ok) throw new Error(`ka10027_state_timeline: HTTP ${rTimeline.status}`);

      // 2) 각 응답 JSON 파싱하여 imported 개수 확인
      const [j0c, j0d, j1h, jOpc, jTrade, jLiveEval, jTimeline, jProdLogs, jProdStockLogs] = await Promise.all([
        r0c.json().catch(() => ({} as any)),
        r0d.json().catch(() => ({} as any)),
        r1h.json().catch(() => ({} as any)),
        rOpc.json().catch(() => ({} as any)),
        rTradeStock.json().catch(() => ({} as any)),
        rLiveEval.json().catch(() => ({} as any)),
        rTimeline.json().catch(() => ({} as any)),
        (rProdLogs && rProdLogs.ok) ? rProdLogs.json().catch(() => ({} as any)) : Promise.resolve({} as any),
        (rProdStockLogs && rProdStockLogs.ok) ? rProdStockLogs.json().catch(() => ({} as any)) : Promise.resolve({} as any),
      ]);
      const parseImported = (data: any): number => {
        if (!data || typeof data !== 'object') return 0;
        if (typeof data.imported === 'number') return data.imported;
        if (typeof data.count === 'number') return data.count;
        if (data.extra && typeof data.extra.totalProcessed === 'number') return data.extra.totalProcessed;
        return 0;
      };
      const parseTotal = (data: any): number | undefined => {
        if (!data || typeof data !== 'object') return undefined;
        if (typeof data.total === 'number') return data.total;
        if (data.extra && typeof data.extra.totalRedisKeyCount === 'number') return data.extra.totalRedisKeyCount;
        return undefined;
      };
      const dailyCount = parseImported(response);
      setDataCount(dailyCount);

      const prodLogsLocalDir = (jProdLogs && typeof jProdLogs.localDir === 'string') ? jProdLogs.localDir : '';
      const prodStockLogsLocalDir = (jProdStockLogs && typeof jProdStockLogs.localDir === 'string') ? jProdStockLogs.localDir : '';
      const details = [
        { table: `${tableName} (RDS → bt:kiwoom:daily:ranking)`, imported: dailyCount, total: parseTotal(response) },
        { table: 'kiwoom_orderbook_0c → bt_kiwoom_orderbook_0c', imported: parseImported(j0c), total: parseTotal(j0c) },
        { table: 'kiwoom_orderbook_0d → bt_kiwoom_orderbook_0d', imported: parseImported(j0d), total: parseTotal(j0d) },
        { table: 'kiwoom_1h → bt_kiwoom_1h', imported: parseImported(j1h), total: parseTotal(j1h) },
        { table: 'OPC ka10095 → bt_kiwoom_interested_item_detail', imported: parseImported(jOpc), total: parseTotal(jOpc) },
        { table: 'trade_stock → bt_actual_trade_stock', imported: parseImported(jTrade), total: parseTotal(jTrade) },
        { table: 'live_eval_snapshot → live_eval_snapshot', imported: parseImported(jLiveEval), total: parseTotal(jLiveEval) },
        { table: 'ka10027_state_timeline → ka10027_state_timeline', imported: parseImported(jTimeline), total: parseTotal(jTimeline) },
        { table: `prod logs (app*.log*)${prodLogsLocalDir ? ' → ' + prodLogsLocalDir : ''}`, imported: parseImported(jProdLogs), total: parseTotal(jProdLogs) },
        { table: `prod stock logs (*.log*)${prodStockLogsLocalDir ? ' → ' + prodStockLogsLocalDir : ''}`, imported: parseImported(jProdStockLogs), total: parseTotal(jProdStockLogs) },
      ];
      // 핵심 데이터 임포트(앞 8개)만 allSuccess 판정에 사용; prod 로그는 보조 정보
      const allSuccess = details.slice(0, 8).every((d) => d.imported > 0);
      setLastImportResult({ details, allSuccess });
      if (!allSuccess) {
        const failed = details.slice(0, 6).filter((d) => d.imported === 0).map((d) => d.table).join(', ');
        setCountError(`임포트 실패: 다음 항목이 0건입니다 (prod에 데이터 없음 또는 임포트 오류) → ${failed}`);
      }
      setTimeout(() => {
        onDataImported?.(response);
      }, 1000);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setCountError(errorMessage);
    } finally {
      setCountLoading(false);
    }
  };

  return (
    <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
      <h4 style={{ margin: '0 0 15px 0', color: '#495057' }}>{title}</h4>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{ textAlign: 'center', fontSize: '12px', color: '#6c757d' }}>
              (EST: {formatUTCInputToTZKey(startDateTime, 'America/New_York') || '—'})
            </span>
            <input
              type="text"
              value={startDateTime}
              onChange={(e) => setStartDateTime(e.target.value)}
              placeholder="YYYY-MM-DDTHH:MM:SS"
              style={{ padding: '6px 10px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '14px', width: '200px', textAlign: 'center' }}
            />
            <span style={{ textAlign: 'center', fontSize: '12px', color: '#6c757d' }}>
              (KST: {formatUTCInputToTZKey(startDateTime, 'Asia/Seoul') || '—'})
            </span>
          </div>
        </div>
        <div style={{ alignSelf: 'center', color: '#6c757d', fontSize: '16px' }}>~</div>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{ textAlign: 'center', fontSize: '12px', color: '#6c757d' }}>
              (EST: {formatUTCInputToTZKey(endDateTime, 'America/New_York') || '—'})
            </span>
            <input
              type="text"
              value={endDateTime}
              onChange={(e) => setEndDateTime(e.target.value)}
              placeholder="YYYY-MM-DDTHH:MM:SS"
              style={{ padding: '6px 10px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '14px', width: '200px', textAlign: 'center' }}
            />
            <span style={{ textAlign: 'center', fontSize: '12px', color: '#6c757d' }}>
              (KST: {formatUTCInputToTZKey(endDateTime, 'Asia/Seoul') || '—'})
            </span>
          </div>
        </div>
      </div>
      
      <div style={{ marginTop: '15px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <button 
          onClick={handleDataImport}
          disabled={countLoading || importLoading}
          style={{ width: '100%', padding: '10px 16px', backgroundColor: (countLoading || importLoading) ? '#6c757d' : '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: (countLoading || importLoading) ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: '500' }}
        >
          {(countLoading || importLoading) ? '데이터 임포트중 (prod to dev bt) ...' : '데이터 임포트 (prod to dev bt) - prod에서 돌린 mercury-stock을 dev에서 bt하기위해'}
        </button>
        <button
          onClick={async () => {
            if (!startDateTime || !endDateTime) {
              setCountError('시작시간과 종료시간을 선택해주세요');
              return;
            }
            setCountError(null);
            setExportLoading(true);
            try {
              const ensureZ = (s: string) => (s && !s.endsWith('Z') && !/[-+]\d{2}:\d{2}$/.test(s) ? s + 'Z' : s);
              const res = await fetch('/mercury-backend/bt/export-bt-to-la', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  startDateTime: ensureZ(startDateTime),
                  endDateTime: ensureZ(endDateTime),
                }),
              });
              if (!res.ok) {
                const errText = await res.text();
                throw new Error(`export-bt-to-la 실패: ${res.status} ${errText}`);
              }
              const result = await res.json();
              const details: { table: string; imported: number }[] = [
                { table: 'la:kiwoom:daily:ranking', imported: result.la_daily_ranking_keys ?? 0 },
                { table: 'la:kiwoom:stock:info', imported: result.la_stock_info_keys ?? 0 },
                { table: 'la_kiwoom_orderbook_0c', imported: result.la_orderbook_0c_docs ?? 0 },
                { table: 'la_kiwoom_orderbook_0d', imported: result.la_orderbook_0d_docs ?? 0 },
                { table: 'la_kiwoom_1h', imported: result.la_kiwoom_1h_docs ?? 0 },
                { table: 'la_bt_result_day_trading', imported: result.la_bt_result_day_trading_docs ?? 0 },
              ];
              const total = details.reduce((s, d) => s + d.imported, 0);
              setLastDevToProdResult({ total, details });
            } catch (err) {
              const errorMessage = err instanceof Error ? err.message : 'Unknown error';
              setCountError(errorMessage);
              setLastDevToProdResult(null);
            } finally {
              setExportLoading(false);
            }
          }}
          disabled={exportLoading || process.env.REACT_APP_ENV === 'mercury-frontend-prod'}
          title={process.env.REACT_APP_ENV === 'mercury-frontend-prod' ? '로컬에서만 사용 가능' : undefined}
          style={{
            width: '100%',
            padding: '10px 16px',
            backgroundColor: (exportLoading || process.env.REACT_APP_ENV === 'mercury-frontend-prod') ? '#adb5bd' : '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: (exportLoading || process.env.REACT_APP_ENV === 'mercury-frontend-prod') ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            fontWeight: '500',
          }}
        >
          {exportLoading ? '데이터 엑스포트 (dev bt to prod la) 중...' : '데이터 엑스포트 (dev bt to prod la) - dev에서 돌린 bt를 랜딩페이지에서 사용하기 위해'}
          {process.env.REACT_APP_ENV === 'mercury-frontend-prod' && ' (로컬 전용)'}
        </button>
        {lastDevToProdResult != null && (
          <div style={{ fontSize: '13px', color: '#155724', marginTop: '4px', whiteSpace: 'pre-line' }}>
            {lastDevToProdResult.details.map(d => `${d.imported.toLocaleString()}건: ${d.table}`).join('\n')}
          </div>
        )}
      </div>
      
      <div style={{
        marginTop: '15px',
        padding: '12px 16px',
        backgroundColor: lastImportResult && !lastImportResult.allSuccess ? '#f8d7da' : '#d4edda',
        border: lastImportResult && !lastImportResult.allSuccess ? '1px solid #f5c6cb' : '1px solid #c3e6cb',
        borderRadius: '6px',
        fontSize: '14px',
        color: lastImportResult && !lastImportResult.allSuccess ? '#721c24' : '#155724',
        width: '100%',
        maxHeight: '260px',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <div style={{ marginBottom: '6px', fontWeight: '500' }}>
          {lastImportResult ? (
            lastImportResult.allSuccess
              ? <>✅ 임포트 성공 (모든 항목 1건 이상)</>
              : <>❌ 임포트 실패 (0건인 항목 존재)</>
          ) : dataCount !== null ? (
            <>Redis에 임포트 상태: <strong>{dataCount?.toLocaleString() || 0}개</strong></>
          ) : (
            <>Redis에 임포트 상태: <strong>임포트를 시작해주세요</strong></>
          )}
        </div>
        {lastImportResult && (
          <div style={{ fontSize: '12px', marginBottom: '8px', whiteSpace: 'pre-line' }}>
            {lastImportResult.details.map((d) => (
              <div key={d.table} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ color: d.imported > 0 ? '#198754' : '#dc3545', fontWeight: 600, minWidth: 14 }}>{d.imported > 0 ? '✓' : '✗'}</span>
                <span style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                  {d.imported.toLocaleString()}건
                  {d.total !== undefined && <span style={{ color: '#6c757d' }}> / 전체 {d.total.toLocaleString()}건</span>}
                </span>
                <span style={{ color: '#495057' }}>{d.table}</span>
              </div>
            ))}
          </div>
        )}
        <div style={{ fontSize: '11px', color: '#6c757d', marginTop: '8px', lineHeight: '1.4' }}>
          <div style={{ marginBottom: '4px' }}><strong>임포트/엑스포트 작업 상세:</strong></div>
          <div style={{ marginLeft: '8px' }}>
            <div style={{ marginBottom: '6px' }}><strong>[버튼 1] 실제 데이터 임포트 (prod to dev)</strong></div>
            <div style={{ marginLeft: '8px', marginBottom: '8px' }}>
              <div>EC2 프로덕션 환경의 데이터를 로컬 백테스트 환경으로 가져옵니다.</div>
              <div style={{ marginTop: '4px' }}>소스 → 타겟:</div>
              <div style={{ marginLeft: '8px' }}>
                <div>• [prod RDS] kiwoom_ranking_fluctuation_daily → [local Redis] bt:kiwoom:daily:ranking:*</div>
                <div>• [prod MongoDB] kiwoom_orderbook_0c → [local MongoDB] bt_kiwoom_orderbook_0c</div>
                <div>• [prod MongoDB] kiwoom_orderbook_0d → [local MongoDB] bt_kiwoom_orderbook_0d</div>
                <div>• [prod MongoDB] kiwoom_1h → [local MongoDB] bt_kiwoom_1h</div>
                <div>• [prod MongoDB] live_eval_snapshot → [local MongoDB] live_eval_snapshot</div>
                <div>• [prod MongoDB] ka10027_state_timeline → [local MongoDB] ka10027_state_timeline</div>
                <div>• [S3] OPC ka10095 JSONL → [local MySQL] bt_kiwoom_interested_item_detail</div>
                <div>• [prod RDS] trade_stock → [local MySQL] bt_actual_trade_stock</div>
                <div>• [prod mercury EC2] /opt/mercury/backend/logs/app*.log* → [local] ./logs/imported/</div>
                <div>• [prod mercury-stock EC2] /home/ubuntu/mercury-stock/logs/*.log* → [local] ./logs/imported-stock/</div>
              </div>
            </div>
            <div style={{ marginBottom: '6px' }}><strong>[버튼 2] 데이터 엑스포트 (dev bt to prod la)</strong></div>
            <div style={{ marginLeft: '8px', marginBottom: '8px' }}>
              <div>로컬 백테스트 데이터를 프로덕션 랜딩페이지(Lambda)에서 사용할 수 있도록 엑스포트합니다.</div>
              <div style={{ marginTop: '6px', color: '#856404' }}><strong>⚠ 전제 조건</strong> (없으면 랜딩페이지의 KRX 차트 또는 Kiwoom Account 섹션이 빈 상태로 표시됨):</div>
              <div style={{ marginLeft: '8px' }}>
                <div>1. 선택한 시간 범위에 대해 <strong>[버튼 1] 데이터 임포트</strong>를 먼저 실행해서 dev에 데이터가 있어야 함 (KRX 차트 소스: <code>bt:kiwoom:daily:ranking:*</code>)</div>
                <div>2. 같은 시간 범위에 대해 <strong>백테스트가 실행</strong>되어 dev MongoDB <code>bt_result_day_trading</code>에 결과가 저장되어 있어야 함 (Kiwoom Account 섹션 소스)</div>
              </div>
              <div style={{ marginTop: '6px' }}>소스 → 타겟:</div>
              <div style={{ marginLeft: '8px' }}>
                <div>• [local Redis] bt:kiwoom:daily:ranking:* → [prod Redis] la:kiwoom:daily:ranking:*</div>
                <div>• [local Redis] bt:kiwoom:stock:info:* → [prod Redis] la:kiwoom:stock:info:*</div>
                <div>• [local MongoDB] bt_kiwoom_orderbook_0c → [prod MongoDB] la_kiwoom_orderbook_0c</div>
                <div>• [local MongoDB] bt_kiwoom_orderbook_0d → [prod MongoDB] la_kiwoom_orderbook_0d</div>
                <div>• [local MongoDB] bt_kiwoom_1h → [prod MongoDB] la_kiwoom_1h</div>
                <div>• [local MongoDB] bt_result_day_trading → [prod Redis] la:bt_result_day_trading</div>
                <div>• 메타데이터 → [prod Redis] la:export:meta (시간 범위 정보)</div>
              </div>
              <div style={{ marginTop: '4px', color: '#856404' }}>* 기존 prod la:* 키와 la_* 컬렉션은 엑스포트 전에 자동 삭제됩니다.</div>
            </div>
          </div>
        </div>
      </div>
      
      {(countError || importError) && (
        <div style={{ marginTop: '15px', padding: '12px 16px', backgroundColor: '#f8d7da', border: '1px solid #f5c6cb', borderRadius: '6px', fontSize: '14px', color: '#721c24', width: '100%' }}>
          ❌ 오류: {countError || importError}
        </div>
      )}
    </div>
  );
};

export default DataImportSection;
